import { useMutation, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'

import {
  billingErrorMessage,
  billingErrorSchema,
  billingPlanQueryKey,
} from '#components/Settings/Org/useBillingPlan'
import { useInviteData } from '#components/Settings/useInviteData'
import { IOrg, useMst } from '#state'
import { IOrgMember } from '#state/types'

import { seatCapacityQueryKey } from './useSeatCapacity'

export type SeatClaim = { quantity: number }

export type SeatLimit = {
  seatMode: 'dynamic' | 'static'
  seatMax: number | null
  taken: number
  needed: number
}

export class SeatLimitError extends Error {
  constructor(readonly limit: SeatLimit) {
    super('seat limit reached')
    this.name = 'SeatLimitError'
  }
}

const seatLimitOf = (err: unknown): SeatLimit | null => {
  if (!isAxiosError(err) || err.response?.data?.code !== 'SEAT_LIMIT_REACHED') {
    return null
  }
  const data = err.response.data
  return {
    seatMode: data.seatMode === 'static' ? 'static' : 'dynamic',
    seatMax: typeof data.seatMax === 'number' ? data.seatMax : null,
    taken: Number(data.taken) || 0,
    needed: Number(data.needed) || 1,
  }
}

export const seatChangeFailure = (
  err: unknown,
  fallback = 'Failed to change seats',
): { message: string; hostedInvoiceUrl?: string } => {
  const known = billingErrorSchema.safeParse(err)
  if (!known.success) return { message: fallback }

  return {
    message: billingErrorMessage(known.data.body.code),
    hostedInvoiceUrl: known.data.body.hostedInvoiceUrl,
  }
}

const unwrap = async <R extends { status: number; body: unknown }>(
  request: Promise<R>,
): Promise<Extract<R, { status: 200 }>['body']> => {
  const result = await request
  if (result.status !== 200) throw result
  return (result as Extract<R, { status: 200 }>).body
}

const useRefreshSeats = (orgId: string | undefined) => {
  const queryClient = useQueryClient()

  return async () => {
    if (!orgId) return
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: seatCapacityQueryKey(orgId) }),
      queryClient.invalidateQueries({ queryKey: billingPlanQueryKey(orgId) }),
    ])
  }
}

export const useRemoveMember = (orgId: string | undefined) => {
  const refreshSeats = useRefreshSeats(orgId)

  return useMutation({
    mutationFn: (member: IOrgMember) => member.remove(),
    onSuccess: refreshSeats,
  })
}

export const useSeatChanges = (org: IOrg) => {
  const { scrapi } = useMst()
  const queryClient = useQueryClient()
  const refreshSeats = useRefreshSeats(org.id)
  const { invalidateInviteQuery } = useInviteData(org)

  const setSeats = (quantity: number) =>
    unwrap(scrapi.billing.updateSeats({ body: { quantity } }))

  const invite = useMutation({
    async mutationFn({ email, claim }: { email: string; claim?: SeatClaim }) {
      if (claim) await setSeats(claim.quantity)
      try {
        await org.createInvite(email)
      } catch (err) {
        const limit = seatLimitOf(err)
        throw limit ? new SeatLimitError(limit) : err
      }
    },
    async onSettled() {
      await invalidateInviteQuery()
      await queryClient.invalidateQueries({
        queryKey: billingPlanQueryKey(org.id),
      })
    },
  })

  const setReduction = useMutation({
    mutationFn: setSeats,
    onSuccess: refreshSeats,
  })

  return { invite, setReduction }
}
